import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@store';
import dayjs from 'dayjs';

export interface EGESliceState {
  egeAdvertiserId: string;
  egeBSSID: string;
  egeIMEI: string;
  egeIMSI: string;
  egeIP: string;
  egeLocale: string;
  egeSSID: string;
  egeDataRange: null | [number, number];
}

export interface WIFISliceState {
  wifiSSID?: string;
  wifiBSSID?: string;
  wifiAdvertiserIds?: string;
  wifiLanguage?: string;
  wifiCountryCode?: string;
  wifiIP?: string;
  wifiMinScore?: string;
  wifiDataRange: null | [number, number];
  wifiObservationsMustBeConnected: boolean;
  wifiMinimumUniqueAdvertisers: number;
  wifiMinimumObservations: number;
}

export interface AriSliceState {
  ariDateRange: null | [number, number | undefined];
}

export interface SliceFiltersState extends EGESliceState, WIFISliceState, AriSliceState {}

const getInitialAriDateRange = (): [number, number | undefined] => [dayjs().subtract(30, 'day').startOf('day').valueOf(), undefined];

const initialState: SliceFiltersState = {
  // EGE
  egeAdvertiserId: '',
  egeBSSID: '',
  egeIMEI: '',
  egeIMSI: '',
  egeIP: '',
  egeLocale: '',
  egeSSID: '',
  egeDataRange: null,

  // WIFI
  wifiSSID: '',
  wifiBSSID: '',
  wifiAdvertiserIds: '',
  wifiLanguage: '',
  wifiCountryCode: '',
  wifiIP: '',
  wifiMinScore: '',
  wifiDataRange: null,
  wifiObservationsMustBeConnected: false,
  wifiMinimumUniqueAdvertisers: 0,
  wifiMinimumObservations: 0,

  // ARI
  ariDateRange: getInitialAriDateRange()
};

export const slice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    restoreSnapshot: (_, action: PayloadAction<SliceFiltersState>) => {
      return { ...initialState, ...action.payload };
    },
    applyEGEFilters: (state, action: PayloadAction<Partial<EGESliceState>>) => {
      const { egeAdvertiserId, egeBSSID, egeIMSI, egeDataRange, egeLocale, egeSSID, egeIP, egeIMEI } = action.payload;
      if (egeAdvertiserId) {
        state.egeAdvertiserId = egeAdvertiserId;
      }
      if (egeBSSID) {
        state.egeBSSID = egeBSSID;
      }
      if (egeIMSI) {
        state.egeIMSI = egeIMSI;
      }
      if (egeDataRange) {
        state.egeDataRange = egeDataRange;
      }
      if (egeLocale) {
        state.egeLocale = egeLocale;
      }
      if (egeSSID) {
        state.egeSSID = egeSSID;
      }
      if (egeIP) {
        state.egeIP = egeIP;
      }
      if (egeIMEI) {
        state.egeIMEI = egeIMEI;
      }
    },
    resetEGEFilters: (state) => {
      state.egeAdvertiserId = '';
      state.egeBSSID = '';
      state.egeIMEI = '';
      state.egeIMSI = '';
      state.egeIP = '';
      state.egeLocale = '';
      state.egeSSID = '';
      state.egeDataRange = null;
    },
    applyWIFIFilters: (state, action: PayloadAction<Partial<WIFISliceState>>) => {
      const {
        wifiSSID,
        wifiBSSID,
        wifiAdvertiserIds,
        wifiLanguage,
        wifiCountryCode,
        wifiIP,
        wifiDataRange,
        wifiObservationsMustBeConnected,
        wifiMinimumUniqueAdvertisers,
        wifiMinimumObservations,
        wifiMinScore
      } = action.payload;
      state.wifiSSID = wifiSSID;
      state.wifiBSSID = wifiBSSID;
      state.wifiAdvertiserIds = wifiAdvertiserIds;
      state.wifiLanguage = wifiLanguage;
      state.wifiCountryCode = wifiCountryCode;
      state.wifiIP = wifiIP;
      state.wifiMinScore = wifiMinScore;

      if (wifiDataRange) {
        state.wifiDataRange = wifiDataRange;
      }
      if (wifiObservationsMustBeConnected) {
        state.wifiObservationsMustBeConnected = wifiObservationsMustBeConnected;
      }
      if (wifiMinimumUniqueAdvertisers != undefined && wifiMinimumUniqueAdvertisers >= 0) {
        state.wifiMinimumUniqueAdvertisers = wifiMinimumUniqueAdvertisers;
      }
      if (wifiMinimumObservations != undefined && wifiMinimumObservations >= 0) {
        state.wifiMinimumObservations = wifiMinimumObservations;
      }
    },
    resetWIFIFilters: (state) => {
      state.wifiSSID = '';
      state.wifiBSSID = '';
      state.wifiAdvertiserIds = '';
      state.wifiLanguage = '';
      state.wifiCountryCode = '';
      state.wifiIP = '';
      state.wifiMinScore = '';
      state.wifiDataRange = null;
      state.wifiObservationsMustBeConnected = false;
      state.wifiMinimumUniqueAdvertisers = 0;
      state.wifiMinimumObservations = 0;
    },
    applyAriFilters: (state, action: PayloadAction<Partial<AriSliceState>>) => {
      const { ariDateRange } = action.payload;
      if (ariDateRange) {
        state.ariDateRange = ariDateRange;
      }
    },
    resetAriFilters: (state) => {
      state.ariDateRange = getInitialAriDateRange();
    }
  }
});

const selectEGEAdvertiserId = (state: RootState) => state.filters.egeAdvertiserId;
const selectEGEBSSID = (state: RootState) => state.filters.egeBSSID;
const selectEGEIMSI = (state: RootState) => state.filters.egeIMSI;
const selectEGEDataRange = (state: RootState) => state.filters.egeDataRange;
const selectEGELocale = (state: RootState) => state.filters.egeLocale;
const selectEGESSID = (state: RootState) => state.filters.egeSSID;
const selectEGEIP = (state: RootState) => state.filters.egeIP;
const selectEGEIMEI = (state: RootState) => state.filters.egeIMEI;

const selectWIFISSID = (state: RootState) => state.filters.wifiSSID;
const selectWIFIBSSID = (state: RootState) => state.filters.wifiBSSID;
const selectWIFIAdvertiserIds = (state: RootState) => state.filters.wifiAdvertiserIds;
const selectWIFILanguage = (state: RootState) => state.filters.wifiLanguage;
const selectWIFICountryCode = (state: RootState) => state.filters.wifiCountryCode;
const selectWIFIIP = (state: RootState) => state.filters.wifiIP;
const selectWIFIDataRange = (state: RootState) => state.filters.wifiDataRange;
const selectWIFIObservationsMustBeConnected = (state: RootState) => state.filters.wifiObservationsMustBeConnected;
const selectWIFIMinimumUniqueAdvertisers = (state: RootState) => state.filters.wifiMinimumUniqueAdvertisers;
const selectWIFIMinimumObservations = (state: RootState) => state.filters.wifiMinimumObservations;
const selectWIFIMinScore = (state: RootState) => state.filters.wifiMinScore;

const selectAriDateRange = (state: RootState) => state.filters.ariDateRange;

const egePopoverSelector = createSelector(
  [selectEGEAdvertiserId, selectEGEBSSID, selectEGEIMSI, selectEGEDataRange, selectEGELocale, selectEGESSID, selectEGEIP, selectEGEIMEI],
  (egeAdvertiserId, egeBSSID, egeIMSI, egeDataRange, egeLocale, egeSSID, egeIP, egeIMEI): EGESliceState => ({
    egeAdvertiserId,
    egeBSSID,
    egeIMSI,
    egeDataRange,
    egeLocale,
    egeSSID,
    egeIP,
    egeIMEI
  })
);

const wifiFiltersSelector = createSelector(
  [
    selectWIFISSID,
    selectWIFIBSSID,
    selectWIFIAdvertiserIds,
    selectWIFILanguage,
    selectWIFICountryCode,
    selectWIFIIP,
    selectWIFIDataRange,
    selectWIFIObservationsMustBeConnected,
    selectWIFIMinimumUniqueAdvertisers,
    selectWIFIMinimumObservations,
    selectWIFIMinScore
  ],
  (
    wifiSSID,
    wifiBSSID,
    wifiAdvertiserIds,
    wifiLanguage,
    wifiCountryCode,
    wifiIP,
    wifiDataRange,
    wifiObservationsMustBeConnected,
    wifiMinimumUniqueAdvertisers,
    wifiMinimumObservations,
    wifiMinScore
  ): WIFISliceState => ({
    wifiSSID,
    wifiBSSID,
    wifiAdvertiserIds,
    wifiLanguage,
    wifiCountryCode,
    wifiIP,
    wifiDataRange,
    wifiObservationsMustBeConnected,
    wifiMinimumUniqueAdvertisers,
    wifiMinimumObservations,
    wifiMinScore
  })
);

const ariFiltersSelector = createSelector([selectAriDateRange], (ariDateRange): AriSliceState => ({ ariDateRange }));

const wifiActiveFiltersSelector = createSelector([wifiFiltersSelector], (filters) => Object.values(filters).filter(Boolean).length);

export const sliceFiltersSelectors = {
  egePopoverSelector,
  wifiFiltersSelector,
  wifiActiveFiltersSelector,
  ariFiltersSelector
};

export const {
  //
  restoreSnapshot,
  applyEGEFilters,
  resetEGEFilters,
  applyWIFIFilters,
  resetWIFIFilters,
  applyAriFilters,
  resetAriFilters
} = slice.actions;

export default slice.reducer;
